/**
 * tenantMarketingCopy — the PUBLIC MARKETING surface's copy, per tenant
 * (demo-tenant-environment Task 24, Req 13.1–13.4).
 *
 * WHY THIS IS PART OF THE TASK 23 REGISTRY, NOT A NEW MECHANISM
 * ------------------------------------------------------------
 * Task 23 established one registry — `TENANT_COPY_BY_ID` in `lib/tenantCopy.ts`,
 * keyed by tenant id — as the single place tenant-specific copy lives. This
 * module holds the MARKETING block of that same record: it defines
 * `TenantMarketingCopy` and the three per-tenant values, and `tenantCopy.ts`
 * hangs them off `TenantCopy.marketing`. There is still exactly one lookup and
 * one resolver; a marketing surface reads `useTenantCopy().marketing`.
 *
 * It is a separate FILE only because the marketing block is long-form prose —
 * titleholder bios, team bios, an FAQ list — and mixing several thousand
 * characters of it into the short-copy module would bury the resolution logic
 * that actually needs review.
 *
 * ABSENCE IS RENDERED AS ABSENCE (Req 13.2, 13.3; the Task 23 precedent)
 * ---------------------------------------------------------------------
 * Every list here can be EMPTY and every string can be `''`. That is the whole
 * fallback design: a tenant with no marketing copy renders no tiles, no bios, and
 * no photos — not invented prose, and above all not the original client's. Each
 * consuming surface treats empty as "render the neutral empty state", exactly as
 * Task 22 renders `headerText: ''` rather than inventing a tagline.
 *
 * PHOTOS BELONG TO THE TENANT THAT OWNS THE PEOPLE
 * -----------------------------------------------
 * `image` on a titleholder / team member / sponsor is part of the TENANT's own
 * record. The bundled headshots under `@/images/` are the founding Idaho tenant's
 * real staff and titleholders, so they are referenced ONLY from the Idaho entry.
 * A tenant with no images of its own gets `image: null` (or no entries at all)
 * and its surface renders the empty state — never someone else's people. The
 * files keep their existing names on purpose: renaming an asset would change
 * nothing a viewer sees, while the thing that actually matters (whose faces
 * appear) is decided here, by which tenant resolved.
 */

import bensonImg from '@/images/team/benson.jpg';
import betsyImg from '@/images/team/betsy.jpg';
import meganImg from '@/images/team/megan.jpg';
import timiImg from '@/images/team/timmi.avif';
import jwolfeLogoImg from '@/images/home/JWOLFE PHOTOGRAPHY LOGO.avif';
import missTitleholderHeadshot from '@/images/home/MissIdahoAmerica.jpeg';
import mrsAlternateTitleholderHeadshot from '@/images/home/MrsIdahoAmerican.jpeg';
import mrsTitleholderHeadshot from '@/images/home/MrsIdahoAmerica.jpeg';

/**
 * A run of marketing prose, optionally emphasized.
 *
 * The division cards render one bolded phrase mid-sentence (`<strong>National
 * Mrs. America Stage</strong>`). Modelling that as segments keeps the emphasis in
 * configuration instead of forcing the component to hold half the sentence, and
 * reproduces the existing markup exactly.
 */
export interface TenantProseSegment {
  /** The literal text of this run. */
  text: string;
  /** `true` renders the run bold (`<strong>`). */
  emphasis?: boolean;
}

/** One division/title marketing card on the public About page. */
export interface TenantMarketingDivision {
  /** Stable React key, unique within the tenant. */
  key: string;
  /** Which division group the card belongs under. */
  group: 'MRS' | 'MISS';
  /** `true` for the primary card's dark treatment; `false` for the runner-up's. */
  featured: boolean;
  /** Corner ribbon text, e.g. `'Winner / 1st Place'`. `''` renders no ribbon. */
  badge: string;
  /**
   * The title as DISPLAY LINES. Rendered joined by a line break, which is how
   * these headings have always been typeset — a single string would re-wrap the
   * heading and change the page's appearance.
   */
  titleLines: readonly string[];
  /** The line under the title, e.g. `'Official Preliminary to Mrs. America'`. */
  subtitle: string;
  /** The card's body prose. */
  description: readonly TenantProseSegment[];
}

/** One current titleholder, as the marketing surface presents her. */
export interface TenantTitleholder {
  /** URL fragment used to deep-link this titleholder from the home page. */
  slug: string;
  /** Her name. */
  name: string;
  /** Her title WITHOUT the year, e.g. `'Mrs. Idaho America'`. */
  title: string;
  /** The reign year, rendered after the title. */
  year: string;
  /** One-line platform summary for the home-page tile. */
  platform: string;
  /** Long-form bio. Blank lines are preserved (`whitespace-pre-line`). */
  bio: string;
  /** Her headshot, from THIS tenant's assets. `null` renders no photo. */
  image: string | null;
}

/** One leadership-team member. */
export interface TenantTeamMember {
  /** Their name. */
  name: string;
  /** Their role, e.g. `'Director'`. */
  role: string;
  /** Long-form bio. Blank lines are preserved. */
  bio: string;
  /** Their headshot, from THIS tenant's assets. `null` renders no photo. */
  image: string | null;
  /** `object-position` override for the dialog thumbnail, where the default crops badly. */
  dialogImagePosition?: string;
}

/** One frequently-asked question and its answer. */
export interface TenantFaq {
  /** The question. */
  q: string;
  /** The answer. Blank lines are preserved. */
  a: string;
}

/** A featured sponsor on the marketing home page. */
export interface TenantMarketingSponsor {
  /** The sponsor's name, rendered as the display heading. */
  name: string;
  /** The credit line under the name. */
  credit: string;
  /** The sponsor's site. */
  url: string;
  /** The sponsor's logo, from THIS tenant's assets. */
  image: string;
  /** Alt text for the logo. */
  imageAlt: string;
}

/** The tenant's public social profiles. `null` renders no icon for that network. */
export interface TenantSocialLinks {
  /** Facebook page URL. */
  facebook: string | null;
  /** Instagram profile URL. */
  instagram: string | null;
}

/**
 * Every piece of copy the public marketing surface needs, all resolved — no
 * `undefined` holes for a page to paper over with a literal of its own.
 */
export interface TenantMarketingCopy {
  /** Wordmark tagline under the brand name in the nav and footer. `''` renders none. */
  tagline: string;
  /** One-sentence description of the organization (footer blurb). `''` renders none. */
  orgDescription: string;
  /** The longer "our impact" paragraph on the home page. `''` renders none. */
  impactDescription: string;
  /**
   * The region the tenant represents, as it reads mid-sentence ("the great state
   * of {region}"). `''` selects a region-agnostic phrasing rather than naming
   * somebody else's state.
   */
  region: string;
  /** The home hero's vertical "established" line. `''` renders none. */
  establishedLine: string;
  /**
   * Heading over the division/titles section. Always non-blank — a page needs an
   * `h1` — but tenant-agnostic when no tenant resolved.
   */
  titlesHeading: string;
  /** The contact page's pull-quote, without its quotation marks. `''` renders none. */
  contactQuote: string;
  /** Division/title cards. EMPTY renders no cards. */
  divisions: readonly TenantMarketingDivision[];
  /** Current titleholders. EMPTY renders the empty state, never another tenant's people. */
  titleholders: readonly TenantTitleholder[];
  /** Leadership team. EMPTY renders the empty state, never another tenant's people. */
  team: readonly TenantTeamMember[];
  /** FAQ entries. EMPTY renders the empty state. */
  faqs: readonly TenantFaq[];
  /** Public social profiles. */
  social: TenantSocialLinks;
  /** Featured sponsor, or `null` for none. */
  sponsor: TenantMarketingSponsor | null;
}

/**
 * The neutral marketing copy: nothing but a tenant-agnostic section heading.
 *
 * This is what an unresolved tenant renders (Req 13.2, and Req 2.3's "neutral
 * placeholder, never an original-client literal"). Every list is empty and every
 * prose field is blank, so each page falls to its empty state.
 */
export const NEUTRAL_TENANT_MARKETING: TenantMarketingCopy = Object.freeze({
  tagline: '',
  orgDescription: '',
  impactDescription: '',
  region: '',
  establishedLine: '',
  titlesHeading: 'Our Prestigious Titles',
  contactQuote: '',
  divisions: Object.freeze([]),
  titleholders: Object.freeze([]),
  team: Object.freeze([]),
  faqs: Object.freeze([]),
  social: Object.freeze({ facebook: null, instagram: null }),
  sponsor: null,
}) as TenantMarketingCopy;

/**
 * The founding Idaho tenant's marketing copy — moved here VERBATIM out of
 * `pages/About*.tsx`, `pages/ContactUs.tsx`, the layout chrome, and the home page
 * in `App.tsx`.
 *
 * Verbatim is the point: Idaho's public site is the real product's marketing
 * site, so under the Idaho tenant every one of those pages must render exactly
 * what it rendered before this task. Nothing here is new prose; it is the same
 * prose, reachable only by resolving to Idaho.
 */
export const IDAHO_TENANT_MARKETING: TenantMarketingCopy = Object.freeze({
  tagline: 'The Gem State Legacy',
  orgDescription:
    'Official state pageant for Mrs. Idaho America, American, and Miss Idaho for America Strong.',
  impactDescription:
    'The Idaho Pageants organization is a year-round commitment to personal growth and community service. Our titleholders travel across the state, advocating for their chosen platforms and inspiring thousands of young women.',
  region: 'Idaho',
  establishedLine: 'ESTABLISHED 1984 — BOISE, IDAHO',
  titlesHeading: "Idaho's Prestigious Titles",
  contactQuote:
    'We look forward to hearing from you and helping you take your first steps toward the Idaho America legacy.',
  divisions: Object.freeze([
    Object.freeze({
      key: 'mrs-idaho-america',
      group: 'MRS',
      featured: true,
      badge: 'Winner / 1st Place',
      titleLines: Object.freeze(['Mrs. Idaho', 'America']),
      subtitle: 'Official Preliminary to Mrs. America',
      description: Object.freeze([
        Object.freeze({
          text: "The Mrs. Idaho America competition recognizes Idaho's married women. The winner represents her local community at the state level and continues to the prestigious ",
        }),
        Object.freeze({ text: 'National Mrs. America Stage', emphasis: true }),
        Object.freeze({ text: '.' }),
      ]),
    }),
    Object.freeze({
      key: 'mrs-idaho-american',
      group: 'MRS',
      featured: false,
      badge: '1st Runner Up',
      titleLines: Object.freeze(['Mrs. Idaho', 'American']),
      subtitle: 'Official Preliminary to Mrs. American',
      description: Object.freeze([
        Object.freeze({
          text: 'A title in a league of its own. The First Runner Up is crowned with the American title, earning the opportunity to compete at the ',
        }),
        Object.freeze({ text: 'National Mrs. American Pageant', emphasis: true }),
        Object.freeze({ text: ', with a path to the Mrs. World stage.' }),
      ]),
    }),
    Object.freeze({
      key: 'miss-idaho-for-america-strong',
      group: 'MISS',
      featured: true,
      badge: '',
      titleLines: Object.freeze(['Miss Idaho for', 'America Strong']),
      subtitle: 'Empowering Single, Widowed, & Divorced Women',
      description: Object.freeze([
        Object.freeze({
          text: "Gives the most accomplished single women of our state (18+, widowed, divorced, or never married) the opportunity to compete for Idaho's state title. The winner represents Idaho at the ",
        }),
        Object.freeze({ text: 'National Miss for America Strong Pageant', emphasis: true }),
        Object.freeze({ text: '.' }),
      ]),
    }),
  ]),
  titleholders: Object.freeze([
    Object.freeze({
      slug: 'courtney-lehosit',
      name: 'Courtney Lehosit',
      title: 'Mrs. Idaho America',
      year: '2026',
      platform:
        'Sixth-generation Idahoan, public servant, and cancer advocate lighting the way for Idaho families.',
      image: mrsTitleholderHeadshot,
      bio: `Courtney Lehosit is a sixth-generation Idahoan, public servant, and proud wife and mother dedicated to strengthening families and communities across the state. A former downhill alpine ski racer, she brings the same discipline, resilience, and determination from the slopes to every aspect of her life.

She currently serves as State Scheduler and Office Manager for U.S. Senator Mike Crapo, where she has spent over 15 years working on behalf of Idahoans. Courtney is deeply passionate about supporting families facing cancer—a mission rooted in her own experience after losing her vivacious mother to a short but intense battle with the disease. In 2009, she started The J.W. Conner Foundation, an organization that provides support, resources, and hope to families navigating cancer.

In partnership with her 11-year-old daughter, Penelope, Courtney is also the co-founder of Ignited Candle Co., a purpose-driven business that spreads light to oncology patients across the country, with a portion of all sales donated to support those in active treatment.

As Mrs. Idaho America 2026, Courtney is honored to represent the state she loves and is committed to using her platform to uplift families, advocate for those affected by cancer, and make a meaningful impact throughout Idaho.`,
    }),
    Object.freeze({
      slug: 'lynita-snow',
      name: 'Lynita Snow',
      title: 'Mrs. Idaho American',
      year: '2026',
      platform:
        'A proud military wife and autism advocate dedicated to ensuring no family feels alone.',
      image: mrsAlternateTitleholderHeadshot,
      bio: `Lynita Snow, Mrs. Idaho American 2026, is a devoted wife, proud mother of three, and passionate advocate for those who feel unseen and unsupported. Married for 10 years to her husband, a Marine veteran, Lynita's life is rooted in resilience, service, and unwavering love for her family Carter, Trey, and Emery.

Her journey as a mother has deeply shaped her purpose, especially through raising her son Trey, who is diagnosed with level 2 nonverbal autism. This experience has given Lynita a profound understanding of the challenges many families face in silence, fueling her commitment to be a voice for those who often go unheard.

As Mrs. Idaho American 2026, Lynita's mission is clear: to reach out, uplift, and connect with as many individuals as possible bringing visibility, compassion, and support to those who need it most. Through her platform, "The Unseen and Unsupported," she strives to create meaningful change, inspire others to lead with empathy, and remind every person that they are valued, seen, and never alone.`,
    }),
    Object.freeze({
      slug: 'andrea-dayne-schmutz',
      name: 'Andrea Dayne-Schmutz',
      title: 'Miss Idaho for America Strong',
      year: '2026',
      platform:
        'PhD student, educator, and changemaker dedicated to empowering women through education.',
      image: missTitleholderHeadshot,
      bio: `Andrea is Miss Idaho for America Strong 2026. Driven by her passion for education and equity, her platform, Education Beyond Boundaries, empowers single mothers and immigrant women to pursue formal education and create brighter futures for themselves. A PhD student in Educational Leadership and college instructor for Peace and Conflict Studies, Andrea is also the founder of Empowering Immigrant Women group.

Her academic excellence has been recognized through numerous scholarships and fellowships earned throughout her undergraduate and graduate career. She also brings a global perspective to her work, having completed internships at the British Parliament and Embassy of the Philippines.

Outside of her academic and advocacy work, Andrea enjoys playing the piano, cooking, hiking, traveling, and spending time with her daughter.`,
    }),
  ]),
  team: Object.freeze([
    Object.freeze({
      name: 'Timi Marsh',
      role: 'Director',
      image: timiImg,
      bio: `Idaho born and raised: Timi Marsh is living her dream life as a boy mom to Tripp and Teller - soaking up every moment, monster trucks and all. While most of her days are spent at home with the boys, she also runs her online children's boutique, Harlee Jane - blending motherhood and entrepreneurship with heart.

With a love for the outdoors, motorsports, and a little sparkle from the pageant world - Timi is proud to be a part of the Mrs. Idaho America Pageant team. She married her best friend, J.D. in 2022 and thanks the Lord daily for the life they're building together.

From competitive stick and ball sports, to life at the racetrack - Timi has always been a team player. After her reign as Miss Idaho for America 2021, she stepped into her role as a director for the Idaho America Pageants Organization - where continuing to serve alongside this sisterhood is truly a full-circle moment.`,
    }),
    Object.freeze({
      name: 'Betsy Thiry',
      role: 'Chief Treasurer and Auditor',
      image: betsyImg,
      bio: `Betsy Thiry was born and raised in the Treasure Valley and is a proud graduate of The College of Idaho, where she earned a degree in Biology. After spending time as a high school math teacher, Betsy transitioned to becoming a devoted homeschool mom to her son, Colter. She has been happily married to her husband Nick for nine years, and together they are building a loving, faith-centered family. They welcomed their second child, Scottie, into the world last June.

Betsy has a deep love for Jesus and her heart was profoundly touched during a mission trip to Ethiopia in 2023, where she developed a lasting connection with the people and culture. Her weeks are filled with homeschool, sports, and a newly found love for sourdough baking.`,
    }),
    Object.freeze({
      name: 'Megan Bartschi',
      role: 'Chief Pageant Coordinator',
      image: meganImg,
      bio: `Megan was born in Boston, but grew up here in Idaho and is proud to call it home. She and her husband, Jared, have been married for 12 years and have four children. "Mom" and "Wife" are the titles in life she's most honored to have. She has a son diagnosed with autism and epilepsy and loves being involved in organizations that not only support her son, but other children across Idaho.

Megan grew up loving the outdoors. She enjoys fishing, hunting, horseback riding, and spending time with her family on all types of motorized vehicles. You can find them dirt biking together, jet boating on the Payette River, or snowmobiling in the mountains. While primarily at home with the kids, she assists Jared in his different businesses and investments.

As the former Mrs. Idaho America 2025, Megan fell in love with pageantry and the opportunity it gives women to grow into the best versions of themselves, and now it feels like a full circle moment to give back to the program that gave her so much.`,
    }),
    Object.freeze({
      name: 'Benson Tanner',
      role: 'Chief Technology Officer',
      image: bensonImg,
      dialogImagePosition: 'center center',
      bio: `Benson grew up in Idaho and is proud to call the Treasure Valley home alongside his wife and three children. When he's not wrangling technology, you'll find him skiing the mountains, hiking, hunting, white water rafting, or tinkering with his latest 3D printing or home automation project.

Professionally, Benson is a Principal Data and AI Architect at AWS, where he helps organizations harness the power of artificial intelligence and modern data platforms. His passion for technology runs deep — from enterprise cloud architecture to the smart home gadgets humming away behind the scenes at his house.

Benson and his family are rooted in a strong faith community, and service has always been close to his heart. When his sister was crowned Mrs. Idaho America, it brought the pageant world right to his doorstep — and he's been all in ever since. As Chief Technology Officer, he brings his expertise in AI and data systems to help the Mrs. Idaho America Pageants organization run at its best and continue to grow.`,
    }),
  ]),
  faqs: Object.freeze([
    Object.freeze({
      q: 'I want to compete, where do I start?',
      a: 'Click the apply button at the top of the webpage, and fill out the forms. Once your application has been reviewed and approved by our team, we will provide you with additional details and next steps.',
    }),
    Object.freeze({
      q: 'Is there an entry fee to compete?',
      a: 'Yes. We highly encourage all contestants to reach out to their communities for help in covering the entry fee. The entry fee will cover behind the scenes cost of putting on the pageant, printing tickets and program books, renting the theater for pageant day, etc. Our official program book will have designated space provided to thank those who have sponsored you!',
    }),
    Object.freeze({
      q: 'Do I need previous pageant experience?',
      a: 'Absolutely not! Past contestants have won the title on their first attempt, and other contestants have competed several times before winning the title.',
    }),
    Object.freeze({
      q: 'Do I need to have children in order to compete?',
      a: 'No. We encourage women with, and without children to participate. Former Mrs. Idaho America and Mrs. America winners without children have won their titles.',
    }),
    Object.freeze({
      q: 'Is a platform required to compete for any of these titles?',
      a: 'A platform is something you do for someone or a group that you are passionate about. You do not have to have a platform to participate in the Mrs. Idaho America/Miss Idaho for America Pageant. However, it is always nice to provide service in your community and is a common talking point with judges.\n\nAt the state pageant we will be giving an award for Volunteer/Community Service. This is an optional award and will not determine the outcome of the pageant. Many former, local, and state title holders have promoted their personal platform while holding their titles.',
    }),
    Object.freeze({
      q: 'Do I need a college education to become a titleholder?',
      a: 'No you do not have to have a college education to become Mrs. Idaho America/Mrs. America or Miss Idaho for America/Miss for America. The judges are instructed to not determine the winner based on her educational background. Winners have ranged from having a high school diploma to a PHD.',
    }),
    Object.freeze({
      q: 'Will my age or height affect the outcome?',
      a: "Nope! Contestants have ranged in age from 18-62, have varied in heights from 4'8\u201D to 6'3\u201D",
    }),
    Object.freeze({
      q: 'Can I participate if I was previously married?',
      a: "Contestants that were previously married, but aren't at the date of competition are now able to compete for the title of Miss Idaho for America! Miss Idaho applicants can be divorced, widowed, or never married.\n\nMrs. Idaho contestants must be married as of the date of entry, at least 18 years of age, a person of good moral character, a U.S. Citizen, and a resident of the state of Idaho for at least six months.",
    }),
    Object.freeze({
      q: 'Are appearances required?',
      a: 'Although not required, we highly encourage our local and state titleholders to take part in community outreach and appearances. Businesses, schools, etc. love to host titleholders, and what better way to spread information/awareness on your platform.',
    }),
  ]),
  social: Object.freeze({
    facebook: 'https://www.facebook.com/MrsIdahoPageant/',
    instagram: 'https://www.instagram.com/idahoamericapageants',
  }),
  sponsor: Object.freeze({
    name: 'J.Wolfe Productions',
    credit: 'The official photographer for Idaho Pageants.',
    url: 'http://www.jwolfe208.com',
    image: jwolfeLogoImg,
    imageAlt: 'J.Wolfe Productions — Official Photographer',
  }),
}) as TenantMarketingCopy;

/**
 * The demo Showcase tenant's marketing copy.
 *
 * WHAT IS HERE AND WHAT IS DELIBERATELY EMPTY. The organization-level copy —
 * tagline, descriptions, division cards, FAQ — is Meridian's own, so a prospect
 * browsing the demo's public site reads a coherent Meridian organization rather
 * than a blank shell.
 *
 * `titleholders`, `team`, and `sponsor` are EMPTY, and that is a decision rather
 * than an omission: those entries carry PHOTOGRAPHS OF PEOPLE, and the only
 * headshots in this repository are the real Idaho tenant's actual staff and
 * titleholders. Pointing Meridian at them would put the original client's faces
 * on a sales demo, which is precisely what Req 13.2 forbids. Requirement 11.5
 * requires AI-generated or licensed stock faces, which the Demo_Seed produces
 * (Task 31 / `seed-headshots.ts`) — not something a frontend task can invent. So
 * these render the graceful empty state until that dataset exists.
 */
export const MERIDIAN_TENANT_MARKETING: TenantMarketingCopy = Object.freeze({
  tagline: 'A Tradition of Poise & Purpose',
  orgDescription: 'Official state pageant for Mrs. Meridian and Miss Meridian.',
  impactDescription:
    'The Meridian Pageants organization is a year-round commitment to personal growth and community service. Our titleholders travel across the region, advocating for their chosen platforms and inspiring thousands of young women.',
  // Meridian is a fictional organization, not a state, so it names no region and
  // the region-agnostic phrasing renders instead.
  region: '',
  establishedLine: '',
  titlesHeading: "Meridian's Prestigious Titles",
  contactQuote:
    'We look forward to hearing from you and helping you take your first steps toward the Meridian legacy.',
  divisions: Object.freeze([
    Object.freeze({
      key: 'mrs-meridian',
      group: 'MRS',
      featured: true,
      badge: 'Winner / 1st Place',
      titleLines: Object.freeze(['Mrs.', 'Meridian']),
      subtitle: 'For Married Women',
      description: Object.freeze([
        Object.freeze({
          text: 'The Mrs. Meridian competition recognizes married women across our communities. The winner represents her local community at the state level and continues to the ',
        }),
        Object.freeze({ text: 'national stage', emphasis: true }),
        Object.freeze({ text: '.' }),
      ]),
    }),
    Object.freeze({
      key: 'miss-meridian',
      group: 'MISS',
      featured: true,
      badge: '',
      titleLines: Object.freeze(['Miss', 'Meridian']),
      subtitle: 'Empowering Single, Widowed, & Divorced Women',
      description: Object.freeze([
        Object.freeze({
          text: 'Gives the most accomplished single women of our communities (18+, widowed, divorced, or never married) the opportunity to compete for the state title. The winner represents her state on the ',
        }),
        Object.freeze({ text: 'national stage', emphasis: true }),
        Object.freeze({ text: '.' }),
      ]),
    }),
  ]),
  titleholders: Object.freeze([]),
  team: Object.freeze([]),
  faqs: Object.freeze([
    Object.freeze({
      q: 'I want to compete, where do I start?',
      a: 'Click the apply button at the top of the webpage, and fill out the forms. Once your application has been reviewed and approved by our team, we will provide you with additional details and next steps.',
    }),
    Object.freeze({
      q: 'Is there an entry fee to compete?',
      a: 'Yes. The entry fee covers the behind-the-scenes cost of producing the pageant — the theater, the program book, and pageant-day production. We encourage every contestant to reach out to her community for sponsorship, and our program book sets aside space to thank the sponsors who supported her.',
    }),
    Object.freeze({
      q: 'Do I need previous pageant experience?',
      a: 'Absolutely not. Past contestants have won on their first attempt, and others have competed several times before taking the title.',
    }),
    Object.freeze({
      q: 'Is a platform required to compete?',
      a: 'A platform is the cause you serve and speak for. It is not required, but community service is always welcome and is a common talking point with judges.',
    }),
    Object.freeze({
      q: 'Will my age or height affect the outcome?',
      a: 'No. Our contestants have ranged widely in both, and neither is scored.',
    }),
    Object.freeze({
      q: 'Are appearances required?',
      a: 'Appearances are not required, though we strongly encourage our local and state titleholders to take part in community outreach. Businesses and schools love to host a titleholder, and there is no better way to advance your platform.',
    }),
  ]),
  // No Meridian social profiles exist, so the footer renders no social icons
  // rather than linking a prospect to somebody else's accounts.
  social: Object.freeze({ facebook: null, instagram: null }),
  sponsor: null,
}) as TenantMarketingCopy;
